import * as React from 'react';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ContextMenuPosition } from '../state/useBookingsOrderListContextMenu';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { OrderRow } from '../../../BookingsCalendarTypes';
import { SplitOrderModal } from './SplitOrderModal';
import { Link } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { isPossibleToSplit } from '../state/splitProductUtils';
import { getUser } from '../../../../../../reducers/userReducer';
import * as Features from '../../../../../../shared/Features';
import { FeatureFlagKeys } from '../../../../../../shared/featureFlags/featureFlagKeys';
import { useFeatureFlag } from '../../../../../../shared/featureFlags/useFeatureFlag';
import { CloneOrderFlow } from '../../../../cloneOrder/CloneOrderFlow';

export type BookingsOrderListContextMenuLabels = {
    cloneOrder: string;
    splitOrder: string;
    goToOrder: string;
    description: string;
    submitButton: string;
    canNotSplitOnNoProducts: string;
    canNotSplitAllProducts: string;
    mainProductTitle: string;
    hideOrders: string;
    showOrders: string;
};

type BookingsOrderListContextMenuProps = {
    onClose: () => void;
    position: ContextMenuPosition | null;
    labels: BookingsOrderListContextMenuLabels;
    orderId: number;
    hasMultipleSelection: boolean;
    isOrderHidden: boolean;
    onSubmitSplitOrder: (formData: FormData) => void;
    products: OrderRow[];
    onClickHideOrders: () => void;
    onClickUnHideOrders: () => void;
};

export function BookingsOrderListContextMenu({
    position,
    onClose,
    labels,
    orderId,
    hasMultipleSelection,
    isOrderHidden,
    onSubmitSplitOrder,
    products,
    onClickHideOrders,
    onClickUnHideOrders
}: BookingsOrderListContextMenuProps) {
    let [isSplitOrderActive, setIsSplitOrderActive] = React.useState<boolean>(false);
    let [isCloneOrderActive, setIsCloneOrderActive] = React.useState<boolean>(false);

    let queryClient = useQueryClient();
    let user = useSelector(getUser);
    let isCloneOrderEnabled = useFeatureFlag(FeatureFlagKeys.CloneOrder, false);
    let canCloneOrder = isCloneOrderEnabled && Features.hasFeature(Features.CloneOrders, user);

    function handleCloseSplitOrderModal() {
        setIsSplitOrderActive(false);
    }

    function handleClickCloneOrder() {
        setIsCloneOrderActive(true);
        onClose();
    }

    function handleCloneOrderSuccess() {
        queryClient.invalidateQueries('unbookedOrders');
    }

    function handleClickHideOrder() {
        if (typeof onClickHideOrders === 'function') {
            onClickHideOrders();
        }

        onClose();
    }

    async function handleClickUnHideOrder() {
        if (typeof onClickUnHideOrders === 'function') {
            onClickUnHideOrders();
        }

        onClose();
    }

    return (
        <>
            <Menu
                open={position !== null}
                onClose={onClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    position !== null ? { top: position.mouseY, left: position.mouseX } : undefined
                }
            >
                <MenuItem
                    disabled={!isPossibleToSplit(products)}
                    onClick={() => {
                        setIsSplitOrderActive((previous) => !previous);
                        onClose();
                    }}
                >
                    <SplitscreenIcon sx={{ marginRight: '8px' }} />
                    {labels.splitOrder}
                </MenuItem>
                <MenuItem>
                    <ListAltIcon sx={{ marginRight: '8px' }} />
                    <Link
                        style={{
                            textDecoration: 'none',
                            color: 'inherit'
                        }}
                        to={`/orders/${orderId}/basic`}
                    >
                        {labels.goToOrder}
                    </Link>
                </MenuItem>
                {canCloneOrder && (
                    <MenuItem onClick={handleClickCloneOrder}>
                        <ContentCopyIcon sx={{ marginRight: '8px' }} />
                        <Typography variant="body2">{labels.cloneOrder}</Typography>
                    </MenuItem>
                )}
                <MenuItem
                    disabled={!hasMultipleSelection && isOrderHidden}
                    onClick={handleClickHideOrder}
                >
                    <VisibilityOffIcon sx={{ marginRight: '8px' }} />
                    <Typography variant="body2">{labels.hideOrders}</Typography>
                </MenuItem>
                <MenuItem
                    disabled={!hasMultipleSelection && !isOrderHidden}
                    onClick={handleClickUnHideOrder}
                >
                    <VisibilityIcon sx={{ marginRight: '8px' }} />
                    <Typography variant="body2">{labels.showOrders}</Typography>
                </MenuItem>
            </Menu>
            <SplitOrderModal
                isOpen={isSplitOrderActive}
                onClose={handleCloseSplitOrderModal}
                labels={{
                    title: labels.splitOrder,
                    description: labels.description,
                    submitButton: labels.submitButton,
                    canNotSplitOnNoProducts: labels.canNotSplitOnNoProducts,
                    canNotSplitAllProducts: labels.canNotSplitAllProducts,
                    mainProductTitle: labels.mainProductTitle
                }}
                products={products}
                onSubmit={onSubmitSplitOrder}
                orderId={orderId}
            />
            {canCloneOrder && (
                <CloneOrderFlow
                    orderId={orderId}
                    show={isCloneOrderActive}
                    onHide={() => setIsCloneOrderActive(false)}
                    onSuccess={handleCloneOrderSuccess}
                />
            )}
        </>
    );
}
